/**
 * The two network steps of a scan upload, each reduced to an outcome the queue
 * can act on without knowing HTTP: register the file with Loupe (which says
 * whether S3 already has it, or hands back a signed PUT), then PUT it to S3.
 */

export interface FileDescriptor {
  filename: string;
  size: number;
  /** CRC32, base64 of the big-endian bytes (S3's encoding). */
  crc32: string;
}

export interface RegisteredFile {
  scan_id: string;
  filename: string;
  /** `uploaded`: S3 already holds this exact file, so nothing is sent. */
  status: 'upload' | 'uploaded';
  upload_url?: string;
  upload_headers?: Record<string, string>;
}

export type RegisterOutcome =
  | { kind: 'ok'; files: RegisteredFile[] }
  /** The admin session has gone; retrying before sign-in is pointless. */
  | { kind: 'unauthorized' }
  /** The server refused this file for a reason retrying won't change. */
  | { kind: 'rejected'; message: string }
  /** Network trouble or a server error — worth retrying. */
  | { kind: 'transient'; message: string };

export type PutOutcome =
  | { kind: 'ok' }
  /** The signed URL (or the credentials behind it) expired — get a fresh one. */
  | { kind: 'expired' }
  /** S3 rejected the bytes against the signed CRC32. */
  | { kind: 'checksum' }
  /**
   * S3 refused the request itself (bad signature, access denied…). A fresh URL
   * or a retry would be refused the same way, so the file fails at once with
   * S3's reason rather than hammering Loupe for new URLs.
   */
  | { kind: 'refused'; message: string }
  | { kind: 'transient'; message: string };

async function errorMessage(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json();
    if (typeof body?.error === 'string') return body.error;
  } catch {
    // not JSON — use the fallback
  }
  return fallback;
}

export async function registerScans(
  orderId: string,
  rollId: string,
  files: FileDescriptor[]
): Promise<RegisterOutcome> {
  let res: Response;
  try {
    res = await fetch(`/api/v1/admin/orders/${orderId}/scans`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ files, roll_id: rollId }),
    });
  } catch {
    return { kind: 'transient', message: 'Could not reach Loupe' };
  }

  if (res.status === 401 || res.status === 403) return { kind: 'unauthorized' };
  if (res.status === 404) return { kind: 'rejected', message: 'This roll no longer exists. Reload the page.' };
  if (res.status === 400 || res.status === 409) {
    return { kind: 'rejected', message: await errorMessage(res, 'Loupe refused this file') };
  }
  if (!res.ok) return { kind: 'transient', message: 'Loupe could not prepare the upload' };

  try {
    return { kind: 'ok', files: await res.json() };
  } catch {
    return { kind: 'transient', message: 'Unexpected response from Loupe' };
  }
}

/**
 * How long a PUT may go without sending a byte before it's treated as dead.
 * A dropped connection often never errors — the request just stops moving.
 */
export const STALL_TIMEOUT_MS = 60_000;

/** S3 error codes meaning "these aren't the bytes that were signed". */
const CHECKSUM_CODES = ['BadDigest', 'XAmzContentChecksumMismatch', 'InvalidDigest'];

/** S3 error codes that a freshly signed URL cures. */
const EXPIRED_CODES = ['ExpiredToken', 'TokenRefreshRequired'];

function s3Error(body: string): { code: string | null; message: string | null } {
  const code = /<Code>([^<]+)<\/Code>/.exec(body)?.[1] ?? null;
  const message = /<Message>([^<]+)<\/Message>/.exec(body)?.[1] ?? null;
  return { code, message };
}

/**
 * Whether a presigned URL is past its expiry, read from its own
 * X-Amz-Date/X-Amz-Expires. Used when S3's 403 body can't be read.
 */
export function presignedUrlExpired(url: string, now: number = Date.now()): boolean {
  try {
    const params = new URL(url).searchParams;
    const date = params.get('X-Amz-Date');
    const expires = Number(params.get('X-Amz-Expires'));
    if (!date || !Number.isFinite(expires)) return false;
    const m = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(date);
    if (!m) return false;
    const signedAt = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
    return now >= signedAt + expires * 1000;
  } catch {
    return false;
  }
}

/**
 * The headers to send with a presigned PUT: only those its URL says are
 * signed. Anything else can't help the signature and does harm — every extra
 * header joins the CORS preflight, and S3 refuses a preflight naming a header
 * its CORS rule doesn't allow. (The first production roll failed exactly so:
 * an unsigned `x-amzn-trace-id` from the Lambda environment rode along.)
 */
export function signedHeadersOnly(url: string, headers: Record<string, string>): Record<string, string> {
  let signed: Set<string>;
  try {
    const list = new URL(url).searchParams.get('X-Amz-SignedHeaders');
    if (!list) return headers;
    signed = new Set(list.toLowerCase().split(';'));
  } catch {
    return headers;
  }
  return Object.fromEntries(
    Object.entries(headers).filter(([name]) => {
      const lower = name.toLowerCase();
      return lower !== 'host' && signed.has(lower);
    })
  );
}

export function putFile(
  url: string,
  headers: Record<string, string>,
  file: File,
  onProgress: (percent: number) => void
): Promise<PutOutcome> {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    let settled = false;
    let watchdog: ReturnType<typeof setTimeout> | undefined;

    const settle = (outcome: PutOutcome) => {
      if (settled) return;
      settled = true;
      clearTimeout(watchdog);
      resolve(outcome);
    };
    const armWatchdog = () => {
      clearTimeout(watchdog);
      watchdog = setTimeout(() => {
        // Settle before aborting: abort() fires onabort synchronously.
        settle({ kind: 'transient', message: 'Upload stalled' });
        xhr.abort();
      }, STALL_TIMEOUT_MS);
    };

    xhr.upload.onprogress = (event: ProgressEvent) => {
      armWatchdog();
      if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
    };
    xhr.onload = () => {
      const status = xhr.status;
      if (status >= 200 && status < 300) return settle({ kind: 'ok' });
      const { code, message } = s3Error(typeof xhr.responseText === 'string' ? xhr.responseText : '');
      if (code && CHECKSUM_CODES.includes(code)) return settle({ kind: 'checksum' });
      if (code && EXPIRED_CODES.includes(code)) return settle({ kind: 'expired' });
      if (code === 'AccessDenied' && message && /expired/i.test(message)) {
        return settle({ kind: 'expired' });
      }
      if (status >= 500 || status === 429 || code === 'SlowDown' || code === 'RequestTimeout') {
        return settle({ kind: 'transient', message: `S3 returned ${status}${code ? ` (${code})` : ''}` });
      }
      // Only a URL that really is past its expiry gets re-signed. Treating every
      // 403 as "expired" turned a systematic refusal into a storm of
      // re-registrations — over a thousand in three minutes for one roll.
      if (!code && status === 403 && presignedUrlExpired(url)) return settle({ kind: 'expired' });
      settle({
        kind: 'refused',
        message: `S3 refused the upload (${status}${code ? ` ${code}` : ''})${message ? `: ${message}` : ''}`,
      });
    };
    xhr.onerror = () => settle({ kind: 'transient', message: 'Network error' });
    xhr.ontimeout = () => settle({ kind: 'transient', message: 'Upload timed out' });
    xhr.onabort = () => settle({ kind: 'transient', message: 'Upload interrupted' });

    xhr.open('PUT', url);
    for (const [name, value] of Object.entries(signedHeadersOnly(url, headers))) {
      xhr.setRequestHeader(name, value);
    }
    armWatchdog();
    xhr.send(file);
  });
}
